import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Header } from '../../../../components/Header'
import { ComponentLayout } from '../../../../components/layouts/ComponentLayout'
import { theme } from '../../../../theme/theme'
import type { TimeSlot as TimeSlotType } from '../../../../components/calendar/types'
import { OnClickButton } from '../../../../components/buttons/onclick/OnClickButton'
import { BodyLarge } from '../../../../components/styles/Typography'
import { VideoCamera, Clock, User, ChatBubbles } from '../../../../components/icons'
import { TimeSlotCalendar } from '../../../../components/calendar/TimeSlotCalendar'
import { isMobile } from '../../../../utils/devices'
import { useWindowWidth } from '../../../../hooks/useWindowWidth'
import { useHealthCallStore } from '../../../../state/store'
import { useSelectUserFullName, useSelectBookingFormData } from '../../../../state/selectors'
import { useHealthCallFormValidation } from '../../../../hooks/useHealthCallFormValidation'
import { HealthCallConfirmationModal } from './HealthCallConfirmationModal'
import { HealthCallTimeslotExpiredModal } from './HealthCallTimeslotExpiredModal'
import {
  usePostBookHealthCallQuery,
  useGetHealthCallSummaryQuery,
} from '../../../../api/ester-b2b-api/private/healthCalls/healthCallQueries'
import { usePersonalNumber } from '../../../../hooks/usePersonalNumber'
import { ErrorGuard } from '../../../../components/ErrorGuard'
import { LoadingSpinner } from '../../../../components/LoadingSpinner'
import { QueryKeys } from '../../../../api/ester-b2b-api/types'
import { useQueryClient } from '@tanstack/react-query'
import { useGetSpecialistById } from '../../../../api/contentful/contentfulQueries'
import { useState } from 'react'
import { getLanguageNames } from '../../../../utils/timeSlotCalendar'

export const HealthCallCalendar = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const width = useWindowWidth()
  const isMobileDevice = isMobile(width)

  const { setBookingFormData } = useHealthCallStore()
  const userFullName = useSelectUserFullName()
  const bookingFormData = useSelectBookingFormData()
  const { savePersonalNumber } = usePersonalNumber()

  const queryClient = useQueryClient()
  const { mutate: bookHealthCall, isPending: isBooking } = usePostBookHealthCallQuery()
  const {
    data: healthCallSummary,
    isLoading: isHealthCallSummaryLoading,
    isError: isHealthCallSummaryError,
    error: healthCallSummaryError,
  } = useGetHealthCallSummaryQuery()

  const [selectedSlot, setSelectedSlot] = useState<{ slot: TimeSlotType; date: Date } | undefined>()
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [showTimeslotExpiredModal, setShowTimeslotExpiredModal] = useState(false)

  const { data: specialistData } = useGetSpecialistById(selectedSlot?.slot.specialistId || '', {
    enabled: !!selectedSlot?.slot.specialistId,
  })
  const {
    formData: modalFormData,
    validationErrors,
    isFormValid,
    handlePersonalNumberChange,
    handlePhoneNumberChange,
    validateForm: validateModalForm,
    setFormValues,
    setFieldError,
  } = useHealthCallFormValidation({
    initialValues: {
      personalNumber: bookingFormData.personalNumber,
      phoneNumber: bookingFormData.phoneNumber,
    },
  })

  if (isHealthCallSummaryLoading) {
    return <LoadingSpinner />
  }
  if (!healthCallSummary || isHealthCallSummaryError) {
    return <ErrorGuard error={healthCallSummaryError} />
  }

  const shouldShowDescription = healthCallSummary.remaining > 0

  const specialist = specialistData?.items?.[0]?.fields

  const handleSlotSelect = (slot: TimeSlotType, date: Date) => {
    setSelectedSlot({ slot, date })
  }

  const handleConfirmBooking = () => {
    if (selectedSlot) {
      setFormValues({
        personalNumber: bookingFormData?.personalNumber || '',
        phoneNumber: bookingFormData?.phoneNumber || '',
      })
      setShowConfirmationModal(true)
    }
  }

  const handleTimeslotExpiredClose = () => {
    setShowTimeslotExpiredModal(false)
    setSelectedSlot(undefined)
    queryClient.invalidateQueries({ queryKey: [QueryKeys.TimeSlots] })
  }

  const handleFinalConfirm = () => {
    if (!validateModalForm() || !selectedSlot) {
      return
    }
    savePersonalNumber({
      personalNumber: modalFormData.personalNumber,
      isValid: !validationErrors.personalNumber,
      setError: (message) => setFieldError('personalNumber', message),
      t,
    })

    setBookingFormData({
      personalNumber: modalFormData.personalNumber,
      phoneNumber: modalFormData.phoneNumber,
    })

    bookHealthCall(
      {
        timeslotId: selectedSlot.slot.id,
        phoneNumber: modalFormData.phoneNumber,
        startTime: selectedSlot.slot.startTime.toISOString(),
      },
      {
        onSuccess: () => {
          setShowConfirmationModal(false)
          navigate('/portal/health-services/health-consultation')
        },
        onError: (error) => {
          if (error.message === 'TIMESLOT_TOO_CLOSE') {
            setShowConfirmationModal(false)
            setShowTimeslotExpiredModal(true)
            return
          }
          if (error.message === 'JOURNAL_CONSENT_REQUIRED') {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.Consent] })
            setShowConfirmationModal(false)
            navigate('/portal/health-services/health-consultation')
            return
          }
          setFieldError('phoneNumber', error.message || t('employee.healthCall.bookingError'))
        },
      }
    )
  }

  return (
    <>
      <Header
        title={t('employee.healthCall.calendarTitle')}
        description={shouldShowDescription ? t('employee.healthCall.calendarDescription') : undefined}
        backButtonLink="/portal/health-services/health-consultation"
        backgroundColor={theme.color.beigeLight}
        noPaddingBottom
      />
      <InfoContainer>
        <IconContainer>
          <Clock size={24} color={theme.color.black} />
          <BodyLarge>{t('employee.healthCall.duration')}</BodyLarge>
        </IconContainer>
        <IconContainer>
          <VideoCamera size={24} color={theme.color.black} />
          <BodyLarge>{t('employee.healthCall.meetingType')}</BodyLarge>
        </IconContainer>
        {selectedSlot && specialist && (
          <>
            <IconContainer>
              <User size={24} color={theme.color.black} />
              <BodyLarge>{`${specialist?.firstName || ''} ${specialist?.lastName || ''}, ${specialist?.jobTitlePortal || specialist?.jobTitle || ''}`}</BodyLarge>
            </IconContainer>
            <IconContainer>
              <ChatBubbles size={24} color={theme.color.black} />
              <BodyLarge>{specialist?.languages && getLanguageNames(specialist?.languages, i18n.language)}</BodyLarge>
            </IconContainer>
          </>
        )}
      </InfoContainer>
      <ComponentLayout
        paddingTop={theme.spacing.medium}
        paddingLeft={isMobileDevice ? 0 : undefined}
        paddingRight={isMobileDevice ? 0 : undefined}
        paddingBottom={0}
      >
        <TimeSlotCalendar
          onSlotSelect={handleSlotSelect}
          selectedSlot={selectedSlot ? { slotId: selectedSlot.slot.id, date: selectedSlot.date } : undefined}
        />
      </ComponentLayout>
      <ComponentLayout
        paddingTop={isMobileDevice ? theme.spacing.large : theme.spacing.small}
        backgroundColor={isMobileDevice ? theme.color.white : theme.color.beigeLight}
      >
        <ButtonContainer $isMobile={isMobileDevice}>
          <OnClickButton text={t('common.form.next')} onClick={handleConfirmBooking} disabled={!selectedSlot} />
        </ButtonContainer>
      </ComponentLayout>
      {showConfirmationModal && selectedSlot && (
        <HealthCallConfirmationModal
          isOpen={showConfirmationModal}
          onClose={() => setShowConfirmationModal(false)}
          onConfirm={handleFinalConfirm}
          selectedSlot={selectedSlot}
          userFullName={userFullName}
          formData={modalFormData}
          validationErrors={validationErrors}
          onPersonalNumberChange={handlePersonalNumberChange}
          onPhoneNumberChange={handlePhoneNumberChange}
          isFormValid={isFormValid}
          isLoading={isBooking}
        />
      )}
      <HealthCallTimeslotExpiredModal isOpen={showTimeslotExpiredModal} onClose={handleTimeslotExpiredClose} />
    </>
  )
}

const InfoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.medium}px;
  padding: ${theme.spacing.xsmall}px ${theme.spacing.xlarge}px ${theme.spacing.mediumLarge}px ${theme.spacing.xlarge}px;
  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    flex-wrap: wrap;
    gap: ${theme.spacing.small}px;
    padding: ${theme.spacing.xsmall}px ${theme.spacing.xlarge}px ${theme.spacing.mediumLarge}px
      ${theme.spacing.mediumLarge}px;
  }
`

const IconContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${theme.spacing.tiny}px;
`

const ButtonContainer = styled.div<{ $isMobile: boolean }>`
  display: flex;
  justify-content: ${({ $isMobile }) => ($isMobile ? 'stretch' : 'flex-end')};
  width: 100%;

  button {
    width: ${({ $isMobile }) => ($isMobile ? '100%' : '230px')};
  }
`
